import React, { useState, useEffect } from 'react';
import { 
    Plus, Trash2, Save, X, Beaker, Calculator, 
    Layers, PlusCircle, Edit, Copy, ArrowUp, ArrowDown,
    Search, Filter, CheckCircle, Database, Sparkles,
    Loader2, AlertTriangle, ShieldCheck, Scale, Utensils,
    Wand2, Info, ChevronRight, Zap, RefreshCw, Cpu, Brain,
    Microscope, Activity, Droplets, Thermometer, ClipboardCheck,
    Dumbbell, Target, GraduationCap, ShieldAlert
} from 'lucide-react';
import { StorageService } from '../../services/storageService';
import { generateMathStrategy, generateMathScenarioContent } from '../../services/geminiService';
import { SimulatorScenario, SimulatorCategory, CertificationType } from '../../types';
import { useAuth } from '../../components/AuthBridge';

const CATEGORY_CONFIG: Record<SimulatorCategory, { icon: any, color: string, label: string }> = {
    'weight': { icon: Scale, color: 'text-cyan-500 bg-cyan-50', label: 'Weights & Measures' },
    'nutrition': { icon: Utensils, color: 'text-emerald-500 bg-emerald-50', label: 'Nutrition & Intake' },
    'output': { icon: Droplets, color: 'text-indigo-500 bg-indigo-50', label: 'Clinical Output' },
    'vitals': { icon: Thermometer, color: 'text-rose-500 bg-rose-50', label: 'Vital Metric' },
    'emergency': { icon: Zap, color: 'text-amber-500 bg-amber-50', label: 'Emergency Math' },
    'charting': { icon: ClipboardCheck, color: 'text-slate-600 bg-slate-50', label: 'Reporting / I&O' }
};

export const SimulatorManager: React.FC = () => {
    const { activeCert: currentGlobalCert } = useAuth();
    const [certId, setCertId] = useState('cna');
    const [certs, setCerts] = useState<CertificationType[]>([]);
    const [scenarios, setScenarios] = useState<SimulatorScenario[]>([]);
    const [selectedScenario, setSelectedScenario] = useState<SimulatorScenario | null>(null);
    const [isSaved, setIsSaved] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [status, setStatus] = useState<{type: 'error' | 'success' | 'warning', text: string} | null>(null);

    // AI Strategy / Suggestions
    const [isBlueprinting, setIsBlueprinting] = useState(false);
    const [suggestedTopics, setSuggestedTopics] = useState<string[]>([]);
    
    // Per-scenario AI Synthesis
    const [isSynthesizing, setIsSynthesizing] = useState(false);

    useEffect(() => {
        StorageService.getCertifications().then(data => {
            setCerts(data);
            if (data.length > 0) setCertId(data[0].id);
        });
    }, []);

    useEffect(() => {
        if (!certId) return;
        const load = async () => {
            setIsLoading(true);
            try {
                const data = await StorageService.getSimulatorScenarios(certId);
                setScenarios(data || []);
            } catch (e) {
                console.error("Registry Load Failure:", e);
                setStatus({ type: 'error', text: 'Registry Connection Failure' });
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [certId]);

    const currentCert = certs.find(c => c.id === certId);

    const handleSave = async () => {
        setStatus(null);
        try {
            await StorageService.saveSimulatorScenarios(certId, scenarios);
            setStatus({ type: 'success', text: 'Mathematical registry synchronized.' });
            setIsSaved(true);
            setTimeout(() => setIsSaved(false), 3000);
        } catch (e) {
            setStatus({ type: 'error', text: 'Network bridge failure.' });
        }
    };

    const handleSuggestStrategy = async () => {
        setIsBlueprinting(true);
        setStatus(null);
        try {
            const topics = await generateMathStrategy(currentCert?.name || 'Medical');
            setSuggestedTopics(topics);
            setStatus({ type: 'success', text: 'AI Strategy Synthesized. Expand topics to Provision.' });
        } catch (e) {
            setStatus({ type: 'error', text: 'Strategy engine interrupted.' });
        } finally {
            setIsBlueprinting(false);
        }
    };

    const handleProvisionFromTopic = (topic: string) => {
        const newScenario: SimulatorScenario = {
            id: `sc-suggested-${Date.now()}`,
            category: 'charting',
            title: topic,
            scenarioText: '',
            toolType: 'calculator',
            targetUnit: '',
            correctValue: 0,
            unitLabel: '',
            explanation: '',
            safetyImpact: '',
            stepByStep: ['']
        };
        setScenarios([...scenarios, newScenario]);
        setSelectedScenario(newScenario);
        setSuggestedTopics(suggestedTopics.filter(t => t !== topic));
    };

    const handleFillWithAI = async () => {
        if (!selectedScenario || isSynthesizing) return;
        setIsSynthesizing(true);
        setStatus(null);
        try {
            const logic = await generateMathScenarioContent(selectedScenario.title, currentCert?.name || 'Nursing');
            const updated = { ...selectedScenario, ...logic } as SimulatorScenario;
            updateScenarioInList(updated);
            setStatus({ type: 'success', text: 'Logic synthesized successfully.' });
        } catch (e) {
            setStatus({ type: 'error', text: 'Synthesis Protocol Error' });
        } finally {
            setIsSynthesizing(false);
        }
    };

    const handleAdd = () => {
        const newScenario: SimulatorScenario = {
            id: `sc-${Date.now()}`,
            category: 'weight',
            title: 'New Clinical Calculation',
            scenarioText: '',
            toolType: 'scale',
            targetUnit: 'kg',
            correctValue: 0,
            unitLabel: '',
            explanation: '',
            safetyImpact: '',
            stepByStep: ['']
        };
        setScenarios([...scenarios, newScenario]);
        setSelectedScenario(newScenario);
        setStatus(null);
    };

    const updateScenarioInList = (updated: SimulatorScenario) => {
        setSelectedScenario(updated);
        setScenarios(scenarios.map(s => s.id === updated.id ? updated : s));
    };

    const filteredList = scenarios.filter(s => 
        s.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
        s.category.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-8 animate-fade-in pb-24 h-full flex flex-col text-slate-900">
            
            <div className="flex flex-col lg:flex-row justify-between items-stretch gap-6 bg-slate-900 p-8 rounded-[2.5rem] shadow-2xl border border-white/5 relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:rotate-12 transition-transform duration-1000 pointer-events-none"><Calculator size={160}/></div>
                <div className="relative z-10 flex-1">
                    <div className="flex items-center gap-3 mb-2">
                        <h2 className="text-3xl font-black text-white tracking-tight flex items-center gap-3"><Microscope className="text-indigo-400" /> Simulator Lab</h2>
                        <div className="px-3 py-1 bg-white/5 border border-white/10 rounded-lg flex items-center gap-2">
                            <Database size={10} className="text-slate-500" />
                            <span className="text-[9px] font-black text-emerald-400 tracking-widest">{currentCert?.shortName} Registry</span>
                        </div>
                    </div>
                    <p className="text-slate-400 font-medium text-sm mt-1">Manage interactive clinical math drills and precision metrics.</p>
                    
                    <div className="mt-8 flex flex-wrap gap-3">
                        <div className="bg-white/5 p-1 rounded-2xl border border-white/10 flex items-center shrink-0">
                            <GraduationCap className="ml-3 text-indigo-400" size={16} />
                            <select 
                                value={certId} 
                                onChange={e => setCertId(e.target.value)} 
                                className="bg-transparent text-white px-4 py-2 rounded-xl outline-none font-black text-[10px] cursor-pointer"
                            >
                                {certs.map(c => <option key={c.id} value={c.id} className="text-black">{c.shortName}</option>)}
                            </select>
                        </div>
                        <button 
                            onClick={handleSuggestStrategy} 
                            disabled={isBlueprinting}
                            className="px-6 py-3 bg-indigo-600 text-white rounded-2xl font-black text-[10px] tracking-widest flex items-center gap-2 shadow-xl hover:bg-indigo-50 transition-all flex items-center gap-2 shadow-xl border-b-4 border-indigo-900 active:scale-95 disabled:opacity-50"
                        >
                            {isBlueprinting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} 
                            Suggest Math Protocols
                        </button>
                        <button onClick={handleSave} className={`px-10 py-4 rounded-2xl font-black text-xs tracking-widest flex items-center gap-2 shadow-2xl transition-all ${isSaved ? 'bg-emerald-600 text-white' : 'bg-medical-500 text-white hover:bg-medical-400'} border-b-4 border-black/20`}>
                            {isSaved ? <CheckCircle size={20} /> : <Save size={20} />} {isSaved ? 'Published' : 'Save Lab'}
                        </button>
                    </div>
                </div>
            </div>

            {status && (
                <div className={`p-4 rounded-3xl flex items-center justify-between animate-pop border-2 shadow-xl ${status.type === 'error' ? 'bg-rose-50 border-rose-100 text-rose-700' : status.type === 'warning' ? 'bg-amber-50 border-amber-100 text-amber-700' : 'bg-emerald-50 border-emerald-100 text-emerald-700'}`}>
                    <div className="flex items-center gap-3 ml-2">
                        {status.type === 'error' ? <AlertTriangle size={18}/> : <CheckCircle size={18}/>}
                        <span className="font-black text-xs tracking-widest">{status.text}</span>
                    </div>
                    <button onClick={() => setStatus(null)} className="p-2 hover:bg-black/5 rounded-xl transition-all"><X size={16}/></button>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 flex-1 min-h-0">
                <div className="xl:col-span-3 bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden flex flex-col shadow-sm">
                    <div className="p-6 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                        <span className="font-black text-[10px] tracking-widest text-slate-400">Drills</span>
                        <div className="relative">
                            <Search className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300" size={12} />
                            <input 
                                type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                                className="pl-4 pr-8 py-1.5 bg-white border border-slate-200 rounded-lg text-[10px] outline-none w-28 focus:w-40 transition-all shadow-inner"
                                placeholder="Filter..."
                            />
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 custom-scrollbar space-y-2">
                        {suggestedTopics.length > 0 && (
                            <div className="mb-6 space-y-2 animate-slide-up">
                                <p className="px-2 text-[8px] font-black text-indigo-400 tracking-widest">AI Strategy Suggestions</p>
                                {suggestedTopics.map(topic => (
                                    <button 
                                        key={topic} 
                                        onClick={() => handleProvisionFromTopic(topic)}
                                        className="w-full p-3 bg-indigo-50 border-2 border-dashed border-indigo-200 rounded-xl text-indigo-700 text-[10px] font-bold text-left flex items-center justify-between group hover:bg-white hover:border-indigo-500 transition-all"
                                    >
                                        <span className="truncate">{topic}</span>
                                        <Plus size={12} className="shrink-0 opacity-0 group-hover:opacity-100" />
                                    </button>
                                ))}
                            </div>
                        )}

                        {filteredList.map((s) => {
                            const config = CATEGORY_CONFIG[s.category] || CATEGORY_CONFIG['charting'];
                            const Icon = config.icon;
                            return (
                                <div key={s.id} onClick={() => { setSelectedScenario(s); setStatus(null); }}
                                    className={`p-4 rounded-2xl border-2 transition-all cursor-pointer group flex items-center justify-between ${selectedScenario?.id === s.id ? 'bg-indigo-50 border-indigo-500 shadow-md' : 'bg-white border-transparent hover:bg-slate-50 shadow-sm'}`}>
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className={`p-2 rounded-lg ${selectedScenario?.id === s.id ? 'bg-indigo-600 text-white shadow-lg' : 'bg-slate-100 text-slate-400'}`}>
                                            <Icon size={14} />
                                        </div>
                                        <p className="font-black text-[10px] text-slate-800 truncate">{s.title}</p>
                                    </div>
                                    <button onClick={(e) => { e.stopPropagation(); if(confirm('Delete Lab?')) setScenarios(scenarios.filter(x => x.id !== s.id)); }} className="p-1.5 text-slate-200 hover:text-rose-500 transition-all opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>
                                </div>
                            );
                        })}
                        <button onClick={handleAdd} className="w-full py-4 border-2 border-dashed border-slate-100 rounded-2xl text-[10px] font-black text-slate-400 hover:text-medical-600 transition-all">+ Add Scenario</button>
                    </div>
                </div>

                {selectedScenario ? (
                    <div className="xl:col-span-9 bg-white rounded-[2.5rem] border border-slate-200 shadow-sm flex flex-col min-h-0 overflow-hidden relative">
                        <div className="flex-1 overflow-y-auto p-8 md:p-10 custom-scrollbar space-y-12">
                            <div className="flex justify-between items-center border-b border-slate-100 pb-8">
                                <div className="flex items-center gap-5">
                                    <div className={`p-4 rounded-2xl shadow-xl ${CATEGORY_CONFIG[selectedScenario.category]?.color || 'bg-slate-900 text-white'}`}>
                                        {React.createElement(CATEGORY_CONFIG[selectedScenario.category]?.icon || Beaker, { size: 28 })}
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-slate-900 tracking-tight leading-none">{selectedScenario.title}</h3>
                                        <p className="text-[10px] font-black text-slate-400 tracking-widest mt-2">{CATEGORY_CONFIG[selectedScenario.category]?.label || 'Math Lab'}</p>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <button 
                                        onClick={handleFillWithAI}
                                        disabled={isSynthesizing}
                                        className="px-6 py-3 bg-slate-900 text-white rounded-2xl font-black text-[9px] tracking-widest flex items-center gap-2 shadow-xl hover:bg-black active:scale-95 disabled:opacity-30"
                                    >
                                        {isSynthesizing ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14}/>} 
                                        Synthesize Logic
                                    </button>
                                    <button onClick={() => setSelectedScenario(null)} className="p-3 bg-slate-50 text-slate-400 rounded-2xl hover:text-rose-500 transition-all border border-slate-100"><X size={20}/></button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                                <div className="lg:col-span-7 space-y-8">
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Scenario Identifier (Title)</label>
                                        <input 
                                            type="text" value={selectedScenario.title} 
                                            onChange={e => updateScenarioInList({...selectedScenario, title: e.target.value})} 
                                            className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl font-black text-lg outline-none focus:bg-white transition-all shadow-inner" 
                                        />
                                    </div>
                                    
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Clinical Narrative (The Problem)</label>
                                        <textarea 
                                            value={selectedScenario.scenarioText} 
                                            onChange={e => updateScenarioInList({...selectedScenario, scenario_text: e.target.value})} 
                                            className="w-full p-6 bg-slate-50 border border-slate-100 rounded-3xl min-h-[160px] font-medium text-slate-800 outline-none focus:bg-white resize-none shadow-inner leading-relaxed" 
                                            placeholder="A patient presents with..."
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2"><Target size={14} className="text-indigo-500" /> Calculation Steps</label>
                                        <div className="space-y-2">
                                            {(selectedScenario.stepByStep || []).map((step, idx) => (
                                                <div key={idx} className="flex gap-2 group animate-pop">
                                                    <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center font-black text-[10px] text-indigo-600 shrink-0 shadow-sm">{idx + 1}</div>
                                                    <input 
                                                        type="text" value={step} 
                                                        onChange={e => {
                                                            const next = [...(selectedScenario.stepByStep || [])];
                                                            next[idx] = e.target.value;
                                                            updateScenarioInList({...selectedScenario, stepByStep: next});
                                                        }}
                                                        className="flex-1 px-5 py-3 bg-white border border-slate-200 rounded-xl font-bold text-xs outline-none focus:border-indigo-500"
                                                    />
                                                    <button onClick={() => {
                                                        const next = (selectedScenario.stepByStep || []).filter((_, i) => i !== idx);
                                                        updateScenarioInList({...selectedScenario, stepByStep: next});
                                                    }} className="p-3 text-slate-200 hover:text-rose-500 transition-all opacity-0 group-hover:opacity-100"><X size={16}/></button>
                                                </div>
                                            ))}
                                            <button 
                                                onClick={() => updateScenarioInList({...selectedScenario, stepByStep: [...(selectedScenario.stepByStep || []), ""]})}
                                                className="w-full py-3 border-2 border-dashed border-indigo-100 rounded-xl text-[9px] font-black text-indigo-400 hover:bg-indigo-50 transition-all"
                                            >+ Add Logic Phase</button>
                                        </div>
                                    </div>
                                </div>

                                <div className="lg:col-span-5 space-y-8">
                                    <div className="bg-slate-50 p-8 rounded-[3rem] border border-slate-100 space-y-8 shadow-inner">
                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Classification</label>
                                                <select 
                                                    value={selectedScenario.category} 
                                                    onChange={e => updateScenarioInList({...selectedScenario, category: e.target.value as any})}
                                                    className="w-full p-4 bg-white border border-slate-200 rounded-2xl font-black text-[10px] outline-none shadow-sm cursor-pointer"
                                                >
                                                    {Object.entries(CATEGORY_CONFIG).map(([val, cfg]) => (
                                                        <option key={val} value={val}>{cfg.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Instrument</label>
                                                <select 
                                                    value={selectedScenario.toolType} 
                                                    onChange={e => updateScenarioInList({...selectedScenario, toolType: e.target.value as any})}
                                                    className="w-full p-4 bg-white border border-slate-200 rounded-2xl font-black text-[10px] outline-none shadow-sm cursor-pointer"
                                                >
                                                    <option value="calculator">Calculator</option>
                                                    <option value="scale">Floor Scale</option>
                                                    <option value="graduate">Graduate (mL)</option>
                                                    <option value="meal-tray">Nutrition Tray</option>
                                                    <option value="chart">Clinical Chart</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Registry Value</label>
                                                <input 
                                                    type="number" step="0.01" value={selectedScenario.correctValue} 
                                                    onChange={e => updateScenarioInList({...selectedScenario, correctValue: parseFloat(e.target.value)})} 
                                                    className="w-full p-4 bg-white border-2 border-indigo-500 rounded-2xl font-black text-2xl tabular-nums text-indigo-600 shadow-xl" 
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Target Unit</label>
                                                <input 
                                                    type="text" value={selectedScenario.unitLabel} 
                                                    onChange={e => updateScenarioInList({...selectedScenario, unitLabel: e.target.value})} 
                                                    className="w-full p-4 bg-white border border-slate-200 rounded-2xl font-black text-xs" 
                                                    placeholder="kg / oz / mL..."
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-3">
                                            <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2"><Brain size={14} className="text-indigo-400" /> Educational Rationale</label>
                                            <textarea 
                                                value={selectedScenario.explanation} 
                                                onChange={e => updateScenarioInList({...selectedScenario, explanation: e.target.value})} 
                                                className="w-full p-4 bg-white border border-slate-200 rounded-2xl font-medium text-xs md:text-sm resize-none h-24 outline-none shadow-sm" 
                                                placeholder="The student needs to know..."
                                            />
                                        </div>

                                        <div className="p-6 bg-rose-950 rounded-3xl text-white space-y-3 relative overflow-hidden group/alert">
                                            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover/alert:rotate-12 transition-transform duration-700"><AlertTriangle size={64}/></div>
                                            <label className="text-[9px] font-black text-rose-400 tracking-[0.2em] flex items-center gap-2"><ShieldAlert size={14}/> Safety Impact</label>
                                            <textarea 
                                                value={selectedScenario.safetyImpact} 
                                                onChange={e => updateScenarioInList({...selectedScenario, safetyImpact: e.target.value})} 
                                                className="w-full bg-transparent border-none outline-none font-bold text-xs leading-relaxed placeholder:text-rose-900" 
                                                placeholder="Incorrect input result..."
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="xl:col-span-9 flex flex-col items-center justify-center bg-slate-50 rounded-[3rem] border-4 border-dashed border-slate-200 opacity-60">
                        <div className="w-24 h-24 bg-white rounded-[2.5rem] flex items-center justify-center text-slate-200 shadow-xl mb-8 group-hover:scale-[1.1] transition-transform">
                            <Beaker size={48}/>
                        </div>
                        <h4 className="text-xl font-black text-slate-900 tracking-tighter">Mathematical Workspace Ready</h4>
                        <p className="font-black text-slate-400 tracking-[0.3em] text-[10px] mt-2">Provision a new drill or select an existing laboratory to begin orchestration</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const ToggleLeft = ({size}: {size: number}) => (
    <svg width={size} height={size/2} viewBox="0 0 48 24" fill="none"><rect width="48" height="24" rx="12" fill="currentColor" fillOpacity="0.1"/><circle cx="12" cy="12" r="8" fill="white" shadow-lg="true"/></svg>
);

const ToggleRight = ({size}: {size: number}) => (
    <svg width={size} height={size/2} viewBox="0 0 48 24" fill="none"><rect width="48" height="24" rx="12" fill="currentColor"/><circle cx="36" cy="12" r="8" fill="white"/></svg>
);